import { useId } from 'react';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { asciiCode } from '@bookingdei/i18n/ascii-codes';
import { formatPhoneInput, phoneInputDigits, phonePlaceholder } from '@bookingdei/i18n/phone';
import { useLanguage } from './language-context';
import { phonePlan } from '../theme/phone-plan';
import './phone-field.css';

// The console's one mobile-number field (phone spec §2): a fixed dial chip and the
// national number, typed or pasted in any form ("01712…", "{dial} 1712-…", Bangla
// digits). The state is the national digits; callers send `toE164(digits, phonePlan)`
// and check `isValidMobile`. Error text, when the caller has one, is `phone.invalid`.
export function PhoneField({
  id,
  value,
  onChange,
  invalid = false,
  errorText,
  describedBy,
  disabled,
  readOnly,
  autoFocus,
  label,
  onBlur,
  onEnter,
  className,
}: {
  id?: string;
  /** National digits ("1712345678"). */
  value: string;
  onChange: (digits: string) => void;
  invalid?: boolean;
  /** Shown under the field (with `invalid`); the caller decides when (after blur / submit). */
  errorText?: string | null;
  /** The id of a caller-rendered hint the input should be described by. */
  describedBy?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  /** aria-label, when no <label htmlFor> names the input. */
  label?: string;
  onBlur?: () => void;
  onEnter?: () => void;
  className?: string;
}) {
  const { lang } = useLanguage();
  const errId = useId();
  const showError = invalid && !!errorText;
  const described = [describedBy, showError ? errId : null].filter(Boolean).join(' ') || undefined;
  return (
    <>
      <div className={`phone-field${className ? ` ${className}` : ''}`} data-invalid={invalid || undefined} data-disabled={disabled || readOnly || undefined}>
        <span className="phone-field__dial" role="img" aria-label={formatMessage(lang, 'phone.dial_label', { dial: asciiCode(phonePlan.dial) })}>
          {phonePlan.dial}
        </span>
        <input
          id={id}
          className="phone-field__input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={phonePlaceholder(phonePlan)}
          value={formatPhoneInput(value, phonePlan)}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          aria-label={label}
          aria-invalid={invalid || undefined}
          aria-describedby={described}
          onChange={(e) => onChange(phoneInputDigits(e.target.value, phonePlan))}
          onBlur={onBlur}
          onKeyDown={
            onEnter
              ? (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onEnter();
                  }
                }
              : undefined
          }
        />
      </div>
      {showError ? (
        <div id={errId} className="phone-field__error" role="alert">
          {errorText}
        </div>
      ) : null}
    </>
  );
}
